import axios, { AxiosError } from 'axios';
import { AppError } from '../models/app-error';
import { userSecretStorageService } from '../ui-services/user-secret-storage.service';

let cancelTokenSource = axios.CancelToken.source();

/**
 * Get auth header.
 * @returns {string|null}
 */
function getAuthHeader() {
  const token = userSecretStorageService.getCurrentSecret();
  return token ? `Bearer ${token}` : null;
}

// Request interceptors for API calls
axios.interceptors.request.use(
  config => {
    config.headers.Authorization = getAuthHeader();
    return config;
  },
  error => Promise.reject(error),
);

// Response interceptor for API calls
axios.interceptors.response.use(
  response => response,
  error => {
    // Use this logic to prevent auto redirect because of the mix of laravel and vue
    const isV2App = window.location.pathname.startsWith('/v2');

    if (error.response?.status === 401 && isV2App) {
      window.location.pathname = '/user/login';
      throw new Error('Unauthorized');
    }
    return Promise.reject(error);
  },
);

/**
 * Cancel previous requests and keep the latest one.
 * @param {string} url url.
 * @param {import('axios').AxiosRequestConfig} config Axios config
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export function cancellableGet(url, config) {
  cancelTokenSource.cancel();
  cancelTokenSource = axios.CancelToken.source();
  return axios.get(url, {
    ...config,
    cancelToken: cancelTokenSource.token,
  });
}

axios.prototype.cancellableGet = cancellableGet;
export const axiosService = axios;

/**
 * Get first error based on backend response.
 * @param {AxiosError} error Http error.
 * @returns {string}
 */
const getFirstError = error => {
  const { errors, message } = error.response.data;

  if (errors != null) {
    const errorsMessages = Object.values(errors);
    const firstError = errorsMessages[0];

    // Because backend return array value instead of string value
    // So that we must get first error via [0]
    return firstError[0];
  }

  // Return message if there is no errors object
  return message;
};

/**
 * Catch http error and return first error.
 * @param {Function} action Action that is supported to get first error
 * @returns {Promise<any>|Error}
 */
export const catchHttpError = async action => {
  try {
    return await action();
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(getFirstError(error));
    }
    throw new Error(error);
  }
};

/**
 * Catch http error and return error object.
 * @param {Function} mapper Mapper.
 * @param {Function} action Action that is supported to get error object
 * @returns {Promise<any|AppError|Error>}
 */
export const catchHttpErrorWithValidationSupport = async (mapper, action) => {
  try {
    return await action();
  } catch (error) {
    if (error instanceof AxiosError) {
      if (error.response?.data?.errors == null) {
        throw new Error(error.response?.data?.message ?? error);
      }
      const { errors } = error.response.data;
      const mapperFunction = typeof mapper === 'function' ? mapper(errors) : mapper.validationErrorFromDto(errors);
      throw new AppError({
        message: error.response.data.message,
        errors: Array.isArray(errors) ? errors : mapperFunction,
      });
    }
    throw new Error(error);
  }
};
